<template>
  <pui-flex class="folder-breadcrumbs__root">
    <pui-common-spinner
      v-if="loading"
      :size="20"
      :strokeWidth="2"
      :hideMessage="true"
      class="folder-breadcrumbs__spinner"
    />
    <span v-else-if="folderPathLabel" :title="folderPathLabel" class="folder-breadcrumbs__path">
      {{ folderPathLabel }}
    </span>
    <span v-else-if="error" :title="errorLabel" class="folder-breadcrumbs__error">
      {{ errorLabel }}
    </span>
  </pui-flex>
</template>

<script>
import Vue from 'vue';

import { getFolderIdsFromPath } from '../../utils/folders';
import { getDamService } from '../../services';

export default {
  props: {
    /**
     * The full path to the folder
     */
    path: {
      type: String,
      required: true,
    },
    /**
     * How many items to display, starting from the end. Give -1 to display all
     */
    lastN: {
      type: Number,
      default: 3,
    },
    /**
     * Separatator to use between folders
     */
    separator: {
      type: String,
      default: ' / ',
    },
    /**
     * The label of the last item to display.
     * If you pass this prop, you won't need to include the ID of the last item
     * in the path
     */
    finalLabel: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      error: false,
      folders: [],
    };
  },
  computed: {
    /**
     * @returns {string[]} all the folder ids in the path
     */
    folderIds() {
      return getFolderIdsFromPath(this.path);
    },
    /**
     * @returns {string[]} the last N folder IDs to display
     */
    lastFolderIds() {
      if (this.lastN <= 0) {
        return this.folderIds;
      }

      const minIndex = Math.max(this.folderIds.length - this.lastN, 0);
      return this.folderIds.slice(minIndex);
    },
    /**
     * @returns {string} the path to the folder
     */
    folderPathLabel() {
      if (this.error) {
        return '';
      }

      const fullLabel = this.folders.map(folder => this.piivoTranslate(folder));
      if (this.finalLabel) {
        fullLabel.push(this.finalLabel);
      }

      return fullLabel.join(this.separator);
    },
    /**
     * @returns {string} label to show when there was a load error
     */
    errorLabel() {
      const fallback = [...this.lastFolderIds];
      if (this.finalLabel) {
        fallback.push(this.finalLabel);
      }
      return fallback.join(this.separator);
    },
  },
  methods: {
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    /**
     * Loads the folder details
     *
     * @param {string} folderId - the id of the folder to load
     * @returns {Promise<object>} the folder
     */
    async loadFolderDetails(folderId) {
      const folderRes = await getDamService('foldersService').getFolder(
        'folderBreadcrumbItem',
        folderId
      );
      return folderRes.body;
    },
    /**
     * Loads the folders in the path
     *
     * @returns {Promise<void>}
     */
    async loadFolders() {
      this.loading = true;

      try {
        // Iterate in reverse order, until we find the parent that "isRoot": true
        for (const folderId of [...this.lastFolderIds].reverse()) {
          const folder = await this.loadFolderDetails(folderId);
          this.folders.unshift(folder);
          if (folder.isRoot) {
            break;
          }
        }
      } catch (err) {
        this.error = true;
        this.$emit('error', err);
      }

      this.loading = false;
    },
  },
  mounted() {
    this.loadFolders();
  },
};
</script>
